import React, { useContext, useMemo } from "react";
import FormBarItem from "./Item";
import { FormBarView } from "data/types";
import { ItemContext, ArrowNavigationContext } from "data/context";
import { ItemStatuses, ItemContextKey } from "data/types";
import { produce } from "immer";

export const useFormBarViews = (defaultViews = [], shouldUseContext = true) => {
    const {
        [ItemContextKey.ItemStatus]: itemStatus,
        [ItemContextKey.HasWorkflow]: hasWorkflow,
    } = useContext(ItemContext) || {};
    const views = useMemo(
        () =>
            produce(defaultViews, (draft) => {
                if (itemStatus & ItemStatuses.Locked) {
                    draft.push(FormBarView.LockInfo);
                }
                if (itemStatus & ItemStatuses.Relation) {
                    draft.push(FormBarView.Relations);
                }
                if (hasWorkflow) {
                    draft.push(FormBarView.Workflow);
                }
            }),
        [defaultViews, itemStatus, hasWorkflow]
    );
    return shouldUseContext ? views : defaultViews;
};

const Factory = ({ views: defaultViews, shouldUseContext, ...props }) => {
    const views = useFormBarViews(defaultViews, shouldUseContext);
    const { setTarget } = useContext(ArrowNavigationContext) || {};
    return views.map((targetView, i) => {
        switch (targetView) {
            case FormBarView.Document:
            case FormBarView.Upload:
            case FormBarView.ItemFlows:
            case FormBarView.DocModal:
            case FormBarView.Folder:
                return props.isEdit ? (
                    <FormBarItem.Edit
                        key={`${targetView}.edit.${i}`}
                        targetView={targetView}
                        setTarget={setTarget}
                        {...props}
                    />
                ) : (
                    <FormBarItem.View
                        key={`${targetView}.view.${i}`}
                        targetView={targetView}
                        setTarget={setTarget}
                        {...props}
                    />
                );
            case FormBarView.Search:
                return (
                    <FormBarItem.Search
                        key={`${targetView}.${i}`}
                        setTarget={setTarget}
                        {...props}
                    />
                );
            case FormBarView.SearchScope:
                return (
                    <FormBarItem.SearchScope
                        key={`${targetView}.${i}`}
                        {...props}
                    />
                );
            case FormBarView.LockInfo:
                return (
                    <FormBarItem.LockInfo
                        key={`${targetView}.${i}`}
                        {...props}
                    />
                );
            case FormBarView.ArchiveTarget:
                return (
                    <FormBarItem.ArchiveTarget
                        key={`${targetView}.${i}`}
                        {...props}
                    />
                );
            case FormBarView.Workflow:
                return (
                    <FormBarItem.Workflow
                        key={`${targetView}.${i}`}
                        {...props}
                    />
                );
            case FormBarView.Relations:
                return (
                    <FormBarItem.Relations
                        key={`${targetView}.${i}`}
                        {...props}
                    />
                );
            case FormBarView.Activity:
                return (
                    <FormBarItem.Activity
                        key={`${targetView}.${i}`}
                        {...props}
                    />
                );
            default:
                console.error(`Unknown FormBarView ${targetView}`);
                return null;
        }
    });
};
Factory.displayName = "FormBarFactory";
export default Factory;
